import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useAppSelector } from '@/redux/store';
import { getDataFromLocalStorage } from 'common/useStoredState';

/**
 * Hook to initialize the app language from user's saved preference.
 *
 * Priority order:
 * 0. Feature flag check: If itHubI18N is disabled, skip user preference (English-only mode)
 * 1. User's saved language from the backend (userLanguage field)
 * 2. Language saved in localStorage
 * 3. Default to browser language detection (i18next default behavior)
 *
 * This hook reads from the loggedUserSlice state, which is populated
 * when the /users/me endpoint is called during app initialization.
 */
export const useInitializeLanguageFromUserData = () => {
  const { i18n } = useTranslation();
  const { itHubI18N } = useFlags();

  // Access user language from the logged user state
  const userLanguage = useAppSelector(state => state.loggedUser?.userLanguage);

  useEffect(() => {
    if (itHubI18N === false) {
      return;
    }

    if (userLanguage) {
      // Priority 1: Use language from backend
      if (i18n.language !== userLanguage) {
        i18n.changeLanguage(userLanguage);
      }
    } else {
      // Priority 2: Fallback to localStorage if no backend preference
      const localStorageLanguage = getDataFromLocalStorage('user-language');
      if (localStorageLanguage && i18n.language !== localStorageLanguage) {
        i18n.changeLanguage(localStorageLanguage);
      }
    }
  }, [userLanguage, i18n, itHubI18N]);
};
